/**
 * Hardened IndexedDB open path.
 *
 * Why this exists
 * ---------------
 * iOS/iPadOS Safari (WebKit) backs IndexedDB with SQLite and intermittently
 * fails to open the store, surfacing the verbatim
 *   `UnknownError: unable to load database file on disk`
 * on a *cold* app launch. Standalone (Home-Screen) PWAs are the worst case —
 * they always cold-start with no warm Safari process, and ephemeral origins
 * (e.g. Firebase preview channels) accumulate per-origin storage that WebKit
 * evicts under pressure. The failure comes in two flavours:
 *
 *   1. TRANSIENT — a flaky cold open / resume-from-background that clears when
 *      the DB is re-opened a tick later. A bounded retry recovers it.
 *   2. HARD — a corrupt/evicted/downgrade-trapped file that a retry will never
 *      heal. Only delete-and-recreate recovers it. The server is the source of
 *      truth, so the empty DB re-hydrates via sync.
 *
 * Previously `initLocalDB` was a bare `await db.open()` with no handling, so
 * either flavour propagated to `ChatPage`'s catch, which logged the user out —
 * and logout cannot repair the on-disk file (`clearLocalDB()` itself needs an
 * open DB), so the user was stuck in a permanent login-loop lockout. This module
 * replaces that with retry → delete-and-recreate → a typed terminal error the
 * UI can turn into an actionable recovery screen, plus telemetry so we can see
 * in prod which flavour is actually firing.
 */

import { createLogger } from "@/utils/debug";
import { captureError } from "@/components/error-boundary";
import { safeStorage } from "@/utils/safeStorage";

const log = createLogger("LocalDB");

/** Bounded cold-open retry — covers WebKit's transient open failures. */
export const OPEN_MAX_ATTEMPTS = 4;

/** Backoff between attempts: 150ms, 300ms, 600ms. */
const backoffMs = (attempt: number) => 150 * 2 ** (attempt - 1);

/**
 * Cap on `indexedDB.deleteDatabase`. WebKit's delete never settles while another
 * (e.g. a suspended background) connection to the same DB holds it open —
 * `onblocked` fires but the promise neither resolves nor rejects. Without this
 * cap the awaited delete would hang boot on the loading screen and the recovery
 * screen would never render. On timeout we treat the reset as failed and surface
 * the recovery UI, whose user-initiated retry can succeed once the peer is gone.
 */
export const DELETE_TIMEOUT_MS = 5_000;

/**
 * Best-effort throttle: don't delete-and-recreate more than once per this window
 * after a *successful* wipe, so a persistently re-corrupting origin doesn't wipe
 * and re-sync on every single boot. Best-effort because it is persisted via
 * `safeStorage` — in a storage-blocked WebKit context (private mode, blocked
 * cookies) the write is a no-op and the throttle does not hold. Non-destructive
 * either way: the server is the source of truth, so the only cost is a re-sync.
 */
export const RESET_THROTTLE_MS = 10 * 60_000;
const RESET_AT_KEY = "odb_last_reset_at";

export type OpenErrorCategory =
  // Our `stores()` declaration is malformed — recreating uses the same broken
  // declaration and fails identically, so we must NOT loop on it.
  | "schema-bug"
  // On-disk schema is newer than this build (the no-downgrade trap). Retrying
  // the open is futile; only delete-and-recreate recovers.
  | "version"
  // Dexie can't apply a schema upgrade in place — notably an object-store
  // primary-key change ("Not yet support for changing primary key", e.g. the
  // v8 messages id -> _clientId change hit by clients still on a pre-v8 schema).
  // Deterministic, so retrying is futile; only delete-and-recreate recovers.
  | "upgrade"
  // The reported WebKit signature and its siblings — retry, then reset.
  | "unreadable"
  // Storage is full — reset frees space; retry alone won't.
  | "quota"
  | "other";

export type OpenOutcome =
  | "opened"
  | "recovered-on-retry"
  | "recovered-on-reset";

/**
 * Thrown when the DB could not be opened *or auto-recovered*. Carries the
 * category and the original error as `cause`. Callers should treat this as a
 * "local storage unavailable" state (offer retry / reset), NOT as an auth error.
 */
export class LocalDbOpenError extends Error {
  readonly category: OpenErrorCategory;
  constructor(category: OpenErrorCategory, cause: unknown) {
    super(`Local database could not be opened (category=${category})`, {
      cause,
    });
    this.name = "LocalDbOpenError";
    this.category = category;
  }
}

export const isLocalDbOpenError = (e: unknown): e is LocalDbOpenError =>
  e instanceof LocalDbOpenError ||
  (e as { name?: string } | null)?.name === "LocalDbOpenError";

/**
 * Collect name + message from an error and any Dexie-wrapped `inner`/`cause`,
 * lower-cased, so classification works whether the throw is a raw DOMException
 * or a Dexie error wrapping one.
 */
const errorText = (error: unknown): string => {
  const e = error as
    | {
        name?: string;
        message?: string;
        inner?: { name?: string; message?: string };
        cause?: { name?: string; message?: string };
      }
    | null
    | undefined;
  if (!e) {
    return "";
  }
  return [
    e.name,
    e.message,
    e.inner?.name,
    e.inner?.message,
    e.cause?.name,
    e.cause?.message,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
};

export const classifyOpenError = (error: unknown): OpenErrorCategory => {
  const name = (error as { name?: string } | null)?.name ?? "";
  const text = errorText(error);

  if (name === "SchemaError" || text.includes("schemaerror")) {
    return "schema-bug";
  }
  if (name === "VersionError" || text.includes("versionerror")) {
    return "version";
  }
  if (name === "QuotaExceededError" || text.includes("quota")) {
    return "quota";
  }
  // Dexie schema-upgrade failure (e.g. the in-place primary-key change it
  // refuses). Deterministic — a re-open re-runs the same upgrade and re-throws,
  // so route straight to delete-and-recreate instead of burning retries.
  if (
    name === "UpgradeError" ||
    text.includes("upgradeerror") ||
    text.includes("changing primary key")
  ) {
    return "upgrade";
  }
  // Match on both the top-level name AND the combined text: Dexie wraps the
  // underlying DOMException, so the real name (e.g. "UnknownError") often lives
  // in `.inner`/`.cause` rather than the top-level `.name`.
  if (
    name === "UnknownError" ||
    name === "InvalidStateError" ||
    name === "AbortError" ||
    name === "DataError" ||
    text.includes("unknownerror") ||
    text.includes("invalidstateerror") ||
    text.includes("aborterror") ||
    text.includes("dataerror") ||
    text.includes("unable to load database file on disk") ||
    text.includes("connection to indexed database server lost")
  ) {
    return "unreadable";
  }
  return "other";
};

/** Retry the open in place — only for the transient/unknown families. */
const shouldRetry = (c: OpenErrorCategory) =>
  c === "unreadable" || c === "quota" || c === "other";

/** Delete-and-recreate is safe for everything except our own malformed schema. */
const isResetEligible = (c: OpenErrorCategory) => c !== "schema-bug";

const canResetNow = (now: number): boolean => {
  const raw = safeStorage.get(RESET_AT_KEY);
  if (!raw) {
    return true;
  }
  const last = Number(raw);
  if (!Number.isFinite(last)) {
    return true;
  }
  return now - last >= RESET_THROTTLE_MS;
};

const markReset = (now: number) => safeStorage.set(RESET_AT_KEY, String(now));

/** Minimal surface of a Dexie instance this module needs (keeps it testable). */
export interface OpenableDb {
  name: string;
  open(): Promise<unknown>;
  close(): void;
  delete(): Promise<unknown>;
  /** Highest declared schema version (Dexie's `verno`). */
  verno?: number;
  /**
   * Dexie's `version()` accessor — used by {@link restoreDeclaredSchema} to
   * rebuild the instance's cached schema after a failed upgrade. Optional so
   * unit tests can keep using bare fakes.
   */
  version?(versionNumber: number): {
    stores(schema: Record<string, string | null>): unknown;
  };
}

export interface OpenRecoveryDeps {
  sleep?: (ms: number) => Promise<void>;
  now?: () => number;
  /** Extra cleanup after a destructive reset (e.g. clear orphaned OPFS blobs). */
  afterReset?: () => Promise<void>;
  /** Override the delete timeout (tests). */
  deleteTimeoutMs?: number;
}

const defaultSleep = (ms: number) =>
  new Promise<void>((resolve) => setTimeout(resolve, ms));

/**
 * `db.delete()` capped by a wall-clock timeout (see {@link DELETE_TIMEOUT_MS}).
 * Uses a real cancellable timer rather than the injectable `sleep` so a test
 * that stubs `sleep` to resolve immediately doesn't spuriously trip the timeout.
 * Rejects with `idb-delete-timeout` if the delete hasn't settled in time.
 */
export function deleteDbWithTimeout(
  db: Pick<OpenableDb, "delete">,
  timeoutMs: number = DELETE_TIMEOUT_MS,
): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    let settled = false;
    const timer = setTimeout(() => {
      if (!settled) {
        settled = true;
        reject(new Error("idb-delete-timeout"));
      }
    }, timeoutMs);
    Promise.resolve(db.delete()).then(
      () => {
        if (!settled) {
          settled = true;
          clearTimeout(timer);
          resolve();
        }
      },
      (error) => {
        if (!settled) {
          settled = true;
          clearTimeout(timer);
          reject(error);
        }
      },
    );
  });
}

/**
 * Rebuild the Dexie instance's cached schema from its DECLARED versions.
 *
 * Why this is needed: when a schema upgrade fails mid-walk (e.g. Dexie's
 * `UpgradeError: Not yet support for changing primary key` on the v8
 * `messages` id -> _clientId change), Dexie's upgrade loop has already
 * mutated the instance's internal `_dbSchema` version-by-version — it is left
 * pointing at the INTERMEDIATE version it stopped on, and neither `close()`
 * nor `delete()` restores it. Reopening the same instance after the
 * delete-and-recreate then takes Dexie's fresh-create path (`oldVersion === 0`),
 * which builds the object stores from that stale intermediate schema while
 * still stamping the DB with the latest native version. Result observed in
 * prod: a "v15" DB whose `chats` store has no `hubId` index — the first
 * `chats.where('hubId')` query throws
 * `SchemaError: KeyPath hubId on object store chats is not indexed`.
 *
 * Re-invoking `db.version(verno).stores({})` (public Dexie API) is a no-op on
 * the version declarations themselves, but forces Dexie to recompute the
 * cumulative schema from every declared version — restoring the full latest
 * schema before the reopen. No-op on test fakes without `version`/`verno`.
 */
function restoreDeclaredSchema(db: OpenableDb): void {
  if (
    typeof db.version === "function" &&
    typeof db.verno === "number" &&
    db.verno >= 1
  ) {
    db.version(db.verno).stores({});
  }
}

/**
 * Re-open a (freshly deleted) DB with the same bounded retry/backoff as the
 * initial open, so a transient WebKit flake on the post-delete reopen doesn't
 * needlessly escalate to a terminal failure. Throws the last error if every
 * attempt fails.
 */
async function reopenWithRetries(
  db: OpenableDb,
  sleep: (ms: number) => Promise<void>,
): Promise<void> {
  let lastError: unknown;
  for (let attempt = 1; attempt <= OPEN_MAX_ATTEMPTS; attempt++) {
    try {
      await db.open();
      return;
    } catch (error) {
      lastError = error;
      try {
        db.close();
      } catch {
        // Already closed / never opened — ignore.
      }
      if (attempt < OPEN_MAX_ATTEMPTS) {
        await sleep(backoffMs(attempt));
      }
    }
  }
  throw lastError;
}

/**
 * Open `db`, recovering from WebKit's transient and hard open failures.
 * Resolves with how the open ultimately succeeded; rejects with a
 * {@link LocalDbOpenError} only when neither retry nor a delete-and-recreate
 * could open the database.
 */
export async function openLocalDBWithRecovery(
  db: OpenableDb,
  deps: OpenRecoveryDeps = {},
): Promise<OpenOutcome> {
  const sleep = deps.sleep ?? defaultSleep;
  const now = deps.now ?? (() => Date.now());

  let lastError: unknown;
  let category: OpenErrorCategory = "other";
  let attemptsUsed = 0;

  // Phase 1 — bounded retry for the transient cold-open family.
  for (let attempt = 1; attempt <= OPEN_MAX_ATTEMPTS; attempt++) {
    try {
      await db.open();
      if (attempt > 1) {
        log.warn("open recovered on retry", { attempt });
        void reportOpenTelemetry(
          db.name,
          "recovered-on-retry",
          category,
          attempt,
          lastError,
        );
        return "recovered-on-retry";
      }
      return "opened";
    } catch (error) {
      lastError = error;
      attemptsUsed = attempt;
      category = classifyOpenError(error);
      log.warn("open failed", {
        attempt,
        category,
        name: (error as { name?: string } | null)?.name,
      });

      // Our own malformed schema — recreating won't fix it. Fail fast.
      if (category === "schema-bug") {
        void reportOpenTelemetry(db.name, "terminal", category, attempt, error);
        throw new LocalDbOpenError(category, error);
      }
      // A newer on-disk schema (downgrade trap) can't be retried open — jump
      // straight to the delete-and-recreate phase.
      if (!shouldRetry(category)) {
        break;
      }
      // Release any half-open handle before the next attempt.
      try {
        db.close();
      } catch {
        // Already closed / never opened — ignore.
      }
      if (attempt < OPEN_MAX_ATTEMPTS) {
        await sleep(backoffMs(attempt));
      }
    }
  }

  // Phase 2 — delete-and-recreate (throttled). Server is the source of truth,
  // so the empty DB re-hydrates via sync.
  if (isResetEligible(category) && canResetNow(now())) {
    log.warn("attempting delete-and-recreate recovery", { category });
    try {
      try {
        db.close();
      } catch {
        // Ignore — we're about to delete it anyway.
      }
      await deleteDbWithTimeout(db, deps.deleteTimeoutMs);
      // A failed upgrade leaves the instance's cached schema stuck on the
      // intermediate version it aborted on — rebuild the full declared schema
      // BEFORE the reopen so the fresh DB is not created from it (the
      // "recovered DB missing the chats.hubId index" prod bug).
      restoreDeclaredSchema(db);
      await reopenWithRetries(db, sleep);
      // Mark the throttle only AFTER a successful wipe+reopen, so a failed reset
      // (blocked delete / flaked reopen) doesn't burn the window and leaves the
      // next boot free to retry the recovery.
      markReset(now());
      void reportOpenTelemetry(
        db.name,
        "recovered-on-reset",
        category,
        attemptsUsed,
        lastError,
      );
      if (deps.afterReset) {
        try {
          await deps.afterReset();
        } catch {
          // Best-effort side cleanup — never fail the recovery on it.
        }
      }
      return "recovered-on-reset";
    } catch (resetError) {
      void reportOpenTelemetry(
        db.name,
        "reset-failed",
        category,
        attemptsUsed,
        resetError,
      );
      throw new LocalDbOpenError(category, resetError);
    }
  }

  // Reset not eligible (schema bug) or throttled (already reset recently) —
  // surface a terminal, recoverable failure for the UI to handle.
  void reportOpenTelemetry(
    db.name,
    "terminal",
    category,
    attemptsUsed,
    lastError,
  );
  throw new LocalDbOpenError(category, lastError);
}

// --- Telemetry -------------------------------------------------------------

type TelemetryOutcome =
  | "recovered-on-retry"
  | "recovered-on-reset"
  | "reset-failed"
  | "terminal";

const OUTCOME_ERROR_NAME: Record<TelemetryOutcome, string> = {
  "recovered-on-retry": "IdbOpenRecoveredOnRetry",
  "recovered-on-reset": "IdbOpenRecoveredOnReset",
  "reset-failed": "IdbOpenResetFailed",
  terminal: "IdbOpenTerminal",
};

/**
 * Best-effort environment snapshot for the failure report. Everything here can
 * be absent or throw on locked-down WebKit, so every probe is guarded — a
 * missing field is fine, a thrown telemetry probe is not.
 */
async function collectOpenDiagnostics(dbName: string): Promise<string> {
  const parts: string[] = [];
  // Detect platform inline (not via environmentDetector) so this module — and
  // thus everything importing the DB — doesn't pull that module's module-load
  // window.matchMedia read into its graph.
  try {
    const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
    parts.push(`ios=${/iP(hone|ad|od)/.test(ua) ? 1 : 0}`);
  } catch {
    /* ignore */
  }
  try {
    const standalone =
      window.matchMedia?.("(display-mode: standalone)")?.matches ||
      (navigator as unknown as { standalone?: boolean }).standalone === true;
    parts.push(`standalone=${standalone ? 1 : 0}`);
  } catch {
    /* ignore */
  }
  try {
    if (typeof navigator.storage?.persisted === "function") {
      parts.push(`persisted=${(await navigator.storage.persisted()) ? 1 : 0}`);
    }
  } catch {
    /* ignore */
  }
  try {
    if (typeof navigator.storage?.estimate === "function") {
      const est = await navigator.storage.estimate();
      parts.push(`usage=${est.usage ?? "?"}`, `quota=${est.quota ?? "?"}`);
    }
  } catch {
    /* ignore */
  }
  try {
    const idb = globalThis.indexedDB;
    if (idb && typeof idb.databases === "function") {
      const dbs = await idb.databases();
      const onDisk = dbs.find((d) => d.name === dbName);
      parts.push(`onDiskV=${onDisk?.version ?? "?"}`);
    }
  } catch {
    /* ignore */
  }
  return parts.join(", ");
}

/**
 * Emit an open-path outcome through the existing auto-error-report pipeline
 * (`captureError`), tagged `source: "idb-open"` so it is filterable from real
 * crashes. Carries only the static outcome/category, an attempt count, the
 * WebKit error name/message, and a coarse environment snapshot — never user
 * data. The raw error is attached as `cause` so the pipeline's cause-chain
 * serializer records its exact name/message.
 */
async function reportOpenTelemetry(
  dbName: string,
  outcome: TelemetryOutcome,
  category: OpenErrorCategory,
  attempts: number,
  original: unknown,
): Promise<void> {
  try {
    const diagnostics = await collectOpenDiagnostics(dbName);
    const orig = original as { name?: string; message?: string } | null;
    const err = new Error(
      `[idb-open] outcome=${outcome} category=${category} attempts=${attempts} ` +
        `origin=${orig?.name ?? "?"}: ${orig?.message ?? ""} [${diagnostics}]`,
      // Attach the raw error as `cause` (a WebKit DOMException is NOT an
      // `instanceof Error`, so guard on null, not on Error) — the report
      // pipeline's cause-chain serializer records its exact name/message.
      original != null ? { cause: original } : undefined,
    );
    err.name = OUTCOME_ERROR_NAME[outcome];
    captureError({ error: err, scope: "root", source: "idb-open" });
  } catch {
    // Telemetry must never become a second failure in the open path.
  }
}
